/**
 * Firebase initialization.
 *
 * Reads from VITE_* env vars (see /web/.env, copied from .env.example).
 * If VITE_USE_EMULATORS=true, all SDK clients connect to local emulators
 * instead of production — this is how we run integration tests and local dev.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
  GoogleAuthProvider,
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

// ---------------------------------------------------------------------------
// Required env — fail loudly if missing rather than producing cryptic Firebase
// "auth/invalid-api-key" errors at runtime.
// ---------------------------------------------------------------------------
function requireEnv(name: string): string {
  const value = import.meta.env[name];
  if (!value || typeof value !== 'string') {
    throw new Error(
      `Missing required environment variable: ${name}. ` +
        'Copy .env.example to /web/.env and fill in the Firebase config values.',
    );
  }
  return value;
}

// Same-origin auth helper: Firebase Hosting serves /__/auth/* on EVERY hosting
// domain (web.app + hr-hub.daaily.com). A CROSS-origin authDomain
// (firebaseapp.com while the app runs on web.app / hr-hub) is what Safari + iOS
// storage partitioning breaks: the sign-in completes on the helper origin but
// the app's origin never receives the session → "login needs a hard refresh",
// worst on the installed PWA. Using the app's own hostname keeps the whole
// flow first-party. Localhost/emulator keeps the configured domain.
const configuredAuthDomain = requireEnv('VITE_FIREBASE_AUTH_DOMAIN');
const authDomain =
  typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? window.location.hostname
    : configuredAuthDomain;

export const firebaseConfig = {
  apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
  authDomain,
  projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: requireEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: requireEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: requireEnv('VITE_FIREBASE_APP_ID'),
};

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

export const app: FirebaseApp = initializeApp(firebaseConfig);

// ---------------------------------------------------------------------------
// App Check (reCAPTCHA Enterprise) — attests requests come from the real app,
// not a script wielding a stolen token. Initialised right after the app so its
// token attaches to Firestore/Storage/callable requests.
//
// MONITOR-ONLY for now: server-side enforcement is OFF, so a failing/absent
// token never blocks anyone — the Firebase console → App Check metrics show
// verified vs unverified traffic. Once that looks clean, flip enforcement per
// service (Firestore, Storage, Functions). The site key is a PUBLIC reCAPTCHA
// key (safe in client code). Skipped under emulators (no App Check backend).
// ---------------------------------------------------------------------------
const APPCHECK_SITE_KEY = '6Le6rUItAAAAAFUz4l1UNN-g7q9VIPyvuoWjbtLG';
if (!useEmulators && typeof window !== 'undefined') {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APPCHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    // Never let App Check init break the app while we're in monitor-only mode.
    // eslint-disable-next-line no-console
    console.warn('[firebase] App Check init failed (non-blocking):', e);
  }
}

export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
// All callable functions live in europe-west1 (same region as Firestore eur3).
export const functions: Functions = getFunctions(app, 'europe-west1');

// ---------------------------------------------------------------------------
// Google Sign-In provider — restricted to daaily.com via the `hd` parameter.
// The server side ALSO verifies the email domain after sign-in (don't trust
// the client-side hint alone — see /functions/src/auth/verify.ts).
// ---------------------------------------------------------------------------
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  hd: import.meta.env.VITE_AUTH_HOSTED_DOMAIN ?? 'daaily.com',
  prompt: 'select_account',
});
googleProvider.addScope('email');
googleProvider.addScope('profile');

// ---------------------------------------------------------------------------
// Emulator wiring — only when VITE_USE_EMULATORS=true. Module-evaluated once.
// ---------------------------------------------------------------------------
if (useEmulators) {
  // disableWarnings hides the noisy "running in emulator mode" banner during dev.
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  // eslint-disable-next-line no-console
  console.info('[firebase] Emulators connected.');
}
